import {
  Activity,
  Battery,
  ChevronDown,
  ChevronUp,
  Laptop,
} from "lucide-react";
import React from "react";

const AssetStatusPanel = ({ assets = [], rigCards = [], showDetails = false, selectedRig = null }) => {
  // Initial Rig state data based on the provided context
  const initialRigCards = [    {
      title: "RIG 1",
      value: "CRITICAL",
      label: "High Risk - 4,964 Critical Alarms",
      status: "Critical",children: [
        { name: "Diesel Generator DG-001", status: "Operational", issue: "Fuel 0%", temp: "76.07°C", alarms: "20" },
        { name: "Power Transformer PT-001", status: "Critical", issue: "3,857 critical alarms", temp: "25.77°C", alarms: "3857" },
        { name: "Diesel Storage Tank DST-5000", status: "Critical", issue: "1,107 active + 7,728 historical", temp: "13.34°C", alarms: "1107" }
      ]
    },
    {
      title: "RIG 2",
      value: "CRITICAL",
      label: "2 Critical Issues - Fuel & Leak",
      status: "Critical",      children: [
        { name: "Gas Generator GG-002", status: "Critical", issue: "Critical fuel level 0%", temp: "28.49°C" },
        { name: "Industrial Transformer IT-002", status: "Operational", issue: "No alarms reported", temp: "0.07°C" },
        { name: "Aviation Fuel Tank AFT-002", status: "Critical", issue: "Leak detected", temp: "35.03°C" }
      ]
    },
    {
      title: "RIG 3",
      value: "CRITICAL",
      label: "3 Critical Issues - All Assets",
      status: "Critical",      children: [
        { name: "Hybrid Generator HG-003", status: "Critical", issue: "Critical fuel level 0%", temp: "20°C" },
        { name: "Distribution Transformer DT-003", status: "Critical", issue: "Critical load", temp: "44.85°C" },
        { name: "Gasoline Dispenser Tank GT-003", status: "Critical", issue: "Leak detected", temp: "55.4°C" }
      ]
    }
  ];
  // Use API data if available, otherwise fall back to initial state
  const displayRigCards = (() => {
    const cards = rigCards.length > 0 ? rigCards : initialRigCards;
    // Filter by selectedRig if specified
    return selectedRig ? cards.filter(rig => rig.title === selectedRig) : cards;
  })();
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "operational":
        return "bg-green-500/20 text-green-500 ring-green-500/20";
      case "warning":
        return "bg-amber-500/20 text-amber-500 ring-amber-500/20";
      case "critical":
        return "bg-red-500/20 text-red-500 ring-red-500/20";
      case "maintenance":
        return "bg-blue-500/20 text-blue-500 ring-blue-500/20";
      default:
        return "bg-gray-500/20 text-gray-500 ring-gray-500/20";
    }
  };

  const getBorderColor = (status) => {
    switch (status?.toLowerCase()) {
      case "operational":
        return "border-green-500";
      case "warning":
        return "border-amber-500";
      case "critical":
        return "border-red-500";
      case "maintenance":
        return "border-blue-500";
      default:
        return "border-gray-500";
    }
  };

  const getHealthColor = (health) => {
    if (health >= 80) return "bg-green-500";
    if (health >= 60) return "bg-amber-500";
    return "bg-dashboard-red";
  };

  // Custom ProgressBar component
  const ProgressBar = ({ value, color }) => {
    return (
      <div className="w-full bg-gray-700 rounded-full h-2">
        <div
          className={`h-2 rounded-full ${color || "bg-blue-500"}`}
          style={{ width: `${value}%` }}
        ></div>
        <div className="text-xs text-right mt-1">{value}%</div>
      </div>
    );
  };

  return (
    <div className="p-4 rounded-2xl text-white overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800 border border-neutral-800 shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between pb-4">
        <div className="space-y-1">
          <div className="text-xl font-semibold flex items-center gap-2">
            <Laptop className="h-5 w-5 text-orange-500" />
            Asset Status
          </div>
          <p className="text-sm text-neutral-400">
            Real-time health and performance monitoring
          </p>
        </div>
      </div>      {/* Asset List */}
      <div className="pt-0">
        <div className="space-y-3">
          {/* Display Rig Cards - Compact or Detailed based on showDetails */}
          {displayRigCards.map((rig, index) => (
            <div
              key={index}
              className={`p-3 rounded-lg bg-gray-800/50 border-2 ${getBorderColor(rig.status)} hover:bg-gray-800/80 transition-all duration-200`}
            >
              {/* Rig Header - Always Visible */}
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3 flex-1">
                  <h3 className="font-bold text-base text-white">{rig.title}</h3>
                  <span
                    className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(rig.status)}`}
                  >
                    {rig.value}
                  </span>
                  <p className="text-xs text-neutral-400 flex-1">
                    {rig.label}
                  </p>
                </div>
                <div className="flex items-center">
                  <Activity className={`h-4 w-4 ${rig.status?.toLowerCase() === 'critical' ? 'text-red-500' : 
                    rig.status?.toLowerCase() === 'warning' ? 'text-amber-500' : 'text-green-500'}`} />
                </div>
              </div>

              {/* Child Assets - Only show when showDetails is true */}
              {showDetails && rig.children && rig.children.length > 0 && (
                <div className="space-y-2 mt-3 pt-3 border-t border-gray-700/50">
                  <p className="text-xs text-neutral-400 font-medium mb-2">Assets:</p>
                  {rig.children.map((asset, assetIndex) => (
                    <div
                      key={assetIndex}
                      className={`p-2 rounded-md bg-gray-900/50 border ${getBorderColor(asset.status)} transition-colors duration-200`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-white">{asset.name}</p>
                          <div className="flex items-center gap-4 mt-1">
                            {asset.temp && (
                              <p className="text-xs text-neutral-400">Temp: {asset.temp}</p>
                            )}
                            {asset.issue && asset.issue !== 'None' && (
                              <p className="text-xs text-neutral-400">{asset.issue}</p>
                            )}
                          </div>
                        </div>
                        <span
                          className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(asset.status)}`}
                        >
                          {asset.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Fallback to legacy assets if no Rig cards and no initial data */}
          {displayRigCards.length === 0 && assets.length > 0 && (
            assets.map((asset) => (
              <div
                key={asset.id}
                className="p-3 rounded-lg bg-gray-800/50 border border-neutral-800 hover:bg-gray-800/80 transition-colors duration-200"
              >
                {/* ...existing asset code... */}
              </div>
            ))
          )}

          {/* Empty State */}
          {displayRigCards.length === 0 && assets.length === 0 && (
            <div className="text-center py-8">
              <Laptop className="h-12 w-12 text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-neutral-400 italic">
                No asset data available
              </p>
              <p className="text-xs text-neutral-500 mt-1">
                Assets will appear here when monitoring data is received
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetStatusPanel;
